import React from 'react';
import {Paper, Typography, Button, Box, Grid, Alert} from '@mui/material';
import {
  DataGridPro,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
} from '@mui/x-data-grid-pro';
import {Refresh} from '@mui/icons-material';
import {numericFormatter} from 'react-number-format';
import {usePlanFees} from './usePlanFees';
import {initialFeesColumns, useOngoingFeesColumns} from './columns';
import {ErrorPage} from '../../pages/ErrorPage';
import {themeColors} from '../../theme/Theme';
import {useReviewContext} from '../../contexts/ReviewContext';

const useEditableTable = () => {
  const [cellModesModel, setCellModesModel] = React.useState<GridCellModesModel>({});

  const handleCellClick = React.useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (!params.isEditable) {
        return;
      }

      // Ignore portal
      if (
        (event.target as any).nodeType === 1 &&
        !event.currentTarget.contains(event.target as Element)
      ) {
        return;
      }

      setCellModesModel((prevModel) => {
        return {
          // Revert the mode of the other cells from other rows
          ...Object.keys(prevModel).reduce(
            (acc, id) => ({
              ...acc,
              [id]: Object.keys(prevModel[id]).reduce(
                (acc2, field) => ({
                  ...acc2,
                  [field]: {mode: GridCellModes.View},
                }),
                {}
              ),
            }),
            {}
          ),
          [params.id]: {
            // Revert the mode of other cells in the same row
            ...Object.keys(prevModel[params.id] || {}).reduce(
              (acc, field) => ({...acc, [field]: {mode: GridCellModes.View}}),
              {}
            ),
            [params.field]: {mode: GridCellModes.Edit},
          },
        };
      });
    },
    []
  );

  const handleCellModesModelChange = React.useCallback((newModel: GridCellModesModel) => {
    setCellModesModel(newModel);
  }, []);

  return {cellModesModel, handleCellClick, handleCellModesModelChange};
};

export const frozenFeesMessageFormatter = (isoDateString?: string) => {
  let actualDate = '';

  if (isoDateString) {
    const d = new Date(isoDateString);
    const ds = d.toLocaleDateString('en-GB');
    const ts = d.toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'});
    actualDate = `on ${ds} at ${ts} `;
  }

  return `The non-editable values were frozen ${actualDate}when the review was full submitted.`;
};

export const FeesBreakdown: React.FC = () => {
  const {
    cellModesModel: existingCellModesModel,
    handleCellModesModelChange: existingHandleCellModesModelChange,
    handleCellClick: existingHandleCellClick,
  } = useEditableTable();
  const {
    cellModesModel: postCellModesModel,
    handleCellModesModelChange: postHandleCellModesModelChange,
    handleCellClick: postHandleCellClick,
  } = useEditableTable();
  const {beforeCharge, resetCharge, loading, error, initialCharge, postCharge} =
    usePlanFees();
  const ongoingFeesColumns = useOngoingFeesColumns();
  const {isFullySubmitted, reviewDetails} = useReviewContext();

  if (error) {
    return <ErrorPage inline code="Fees error" text={error} />;
  }

  return (
    <Paper style={{padding: '1rem'}}>
      {initialCharge && (
        <>
          <Typography variant="h4" fontWeight="bold">
            Initial charges
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="h3" fontWeight="bold">
                Total initials
              </Typography>
              <Box border={`1px solid ${themeColors.coral}`} padding={1}>
                <Typography variant="h1" fontWeight="bold">
                  {`£${numericFormatter(String(initialCharge?.totalInitialCharge ?? 0), {
                    decimalScale: 2,
                    thousandSeparator: ',',
                    fixedDecimalScale: true,
                  })}`}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={9}>
              <DataGridPro
                loading={loading}
                rows={initialCharge?.charges || []}
                columns={initialFeesColumns}
                autoHeight
                pinnedRows={{
                  bottom: [initialCharge?.summaryCharge],
                }}
                hideFooter
              />
            </Grid>
          </Grid>
        </>
      )}
      <Box
        borderTop={initialCharge ? `1px solid ${themeColors.grey}` : 'none'}
        pt={initialCharge ? 4 : 0}
        display="flex"
        justifyContent="space-between"
        mt={initialCharge ? 4 : 0}
      >
        <Typography variant="h5" fontWeight="bold">
          <strong>Ongoing charges:</strong>
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            resetCharge();
          }}
          startIcon={<Refresh />}
        >
          Reset ongoing charges
        </Button>
      </Box>
      {isFullySubmitted && (
        <Alert severity="success">
          {frozenFeesMessageFormatter(reviewDetails?.dateSubmitted)}
        </Alert>
      )}
      {beforeCharge && (
        <>
          <Typography variant="h5" mb={1}>
            <strong>Existing ongoing charges</strong>
          </Typography>
          <DataGridPro
            loading={loading}
            rows={beforeCharge?.potCharges || []}
            sx={{
              '& .MuiDataGrid-cell': {
                paddingTop: 0.5,
              },
              grid: {
                bgcolor: 'lightgrey',
              },
            }}
            columns={ongoingFeesColumns}
            autoHeight
            pinnedRows={{
              bottom: [beforeCharge?.summaryCharge],
            }}
            isCellEditable={(params) => {
              const isEditable = params.value?.isEditable;
              return params.id !== beforeCharge?.summaryCharge.id && isEditable;
            }}
            cellModesModel={existingCellModesModel}
            onCellModesModelChange={existingHandleCellModesModelChange}
            onCellClick={existingHandleCellClick}
            hideFooter
          />
        </>
      )}
      {postCharge && (
        <>
          <Typography variant="h5" mb={1} mt={4}>
            <strong>Post Recommendation ongoing charges</strong>
          </Typography>
          <DataGridPro
            loading={loading}
            rows={postCharge?.potCharges || []}
            columns={ongoingFeesColumns}
            autoHeight
            pinnedRows={{
              bottom: [postCharge?.summaryCharge],
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                paddingTop: 0.5,
              },
            }}
            isCellEditable={(params) => {
              const isEditable = params.value?.isEditable;
              return params.id !== postCharge?.summaryCharge.id && isEditable;
            }}
            cellModesModel={postCellModesModel}
            onCellModesModelChange={postHandleCellModesModelChange}
            onCellClick={postHandleCellClick}
            hideFooter
          />
        </>
      )}
    </Paper>
  );
};
